import { useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Chip, ListShell, PersonRow, matches } from "@/components/team/shared";
import { useTeamReport } from "@/components/team/useTeamReport";
import { GOALS, PACE, Pill, VERIFY, weekLabel } from "./shared";

const SUMMARY = [["participants", "Opted in"], ["qualified", "Reward threshold met"], ["mg1_achievers", "20+ MG1s"],
  ["launch_achievers", "1+ launch"], ["on_pace", "On MG1 pace"], ["behind", "Behind MG1 pace"], ["verified", "Leader verified"]];
const ROUTE = { mg1: "20+ MG1s", launch: "Launch", both: "MG1s + launch" };

function Verify({ r, reload }) {
  const v = r.progress.reward.verification;
  const act = async (action) => {
    try { await api.post(`/stp/verify/${r.id}`, { action }); toast.success(action === "verify" ? "Reward verified" : "Verification removed"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update"); }
  };
  if (v === "unverified" && r.progress.reward.qualified)
    return <button onClick={() => act("verify")} data-testid={`stp-verify-${r.id}`} className="text-[11px] font-semibold text-[#0A1F44] underline">Verify reward</button>;
  if (v !== "unverified")
    return <button onClick={() => act("revoke")} data-testid={`stp-revoke-${r.id}`} className="text-[11px] text-slate-500 underline">Remove verification</button>;
  return null;
}

export default function StpTeamView({ scope, q, rowProps }) {
  const { user, isViewAs } = useAuth();
  const [tick, setTick] = useState(0);
  const data = useTeamReport("stp", { scope, tick });
  const [status, setStatus] = useState("all");
  const weeks = data?.rows?.[0]?.progress.weeks || [];
  const curIdx = (weeks.find((w) => w.state === "current") || weeks.filter((w) => w.state === "past").pop() || weeks[0])?.index;
  const [wk, setWk] = useState(null);
  const sel = wk || curIdx;
  const canVerify = !isViewAs && user?.role !== "oversight";
  const rows = useMemo(() => (data?.rows ? data.rows.filter((r) => matches(r, q)
    && (status === "all" || (status === "qualified" ? r.progress.reward.qualified : r.progress.mg1.status === status))) : null), [data, q, status]);
  return (
    <div className="space-y-3">
      {data?.summary && <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="stp-summary">
        {SUMMARY.map(([k, l]) => <div key={k} className="rounded-lg border border-slate-200 p-2"><div className="text-[10px] uppercase tracking-wider text-[#8A6D1F] font-semibold">{l}</div>
          <div className="font-serif text-xl text-[#0A1F44] tabular-nums" data-testid={`stp-sum-${k}`}>{data.summary[k]}</div></div>)}
      </div>}
      <div className="flex flex-wrap gap-2 items-center">
        {[["all", "Everyone"], ["qualified", "Reward met"], ["on_pace", "On pace"], ["behind", "Behind"]].map(([k, l]) =>
          <Chip key={k} active={status === k} onClick={() => setStatus(k)} testid={`stp-status-${k}`}>{l}</Chip>)}
        {weeks.length > 0 && <select value={sel} onChange={(e) => setWk(Number(e.target.value))} data-testid="stp-week-select"
          className="h-8 rounded-full border border-slate-200 px-2 text-xs">{weeks.filter((w) => w.state !== "future").map((w) => <option key={w.index} value={w.index}>{weekLabel(w)}</option>)}</select>}
      </div>
      <ListShell rows={rows} testid="stp-list" empty={q ? "No one matches your search." : "No one in this group has joined STP Run yet."}>
        {(rows || []).map((r) => {
          const p = r.progress; const w = p.weeks.find((x) => x.index === sel) || p.weeks[0];
          return (
            <PersonRow key={r.id} u={r} prefix="stp" {...rowProps}>
              <div className="mt-1.5 space-y-1 text-[11px] text-slate-600" data-testid={`stp-detail-${r.id}`}>
                <div className="flex flex-wrap gap-1.5 items-center">
                  <Pill kind={p.mg1.status} map={PACE} testid={`stp-pace-${r.id}`} />
                  {p.launch.achieved && !p.reward.route && <span className="rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 font-semibold">Launch ✓</span>}
                  {p.reward.route && <span className="rounded-full bg-[#C9A961]/15 text-[#6B5A1D] px-2 py-0.5 font-semibold" data-testid={`stp-route-${r.id}`}>{ROUTE[p.reward.route]}</span>}
                  <Pill kind={p.reward.verification} map={VERIFY} testid={`stp-vstatus-${r.id}`} />
                  {canVerify && <Verify r={r} reload={() => setTick((t) => t + 1)} />}
                </div>
                <div className="tabular-nums">Week {w.index}: {GOALS.map(([k, l]) => `${w.actuals[k]}/${w.goals[k]} ${l}`).join(" · ")}{w.all_met ? " · all goals met" : ""}</div>
                <div className="tabular-nums text-slate-500">Run to date: MG1s {p.mg1.count}/20 · launches {p.launch.count} · {p.totals.conversations} conv · {p.totals.contact_exchanges} CEs · {p.totals.dtms} DTMs</div>
              </div>
            </PersonRow>
          );
        })}
      </ListShell>
      {data?.weekly?.length > 0 && <details className="text-xs" data-testid="stp-weekly-team">
        <summary className="cursor-pointer text-[#0A1F44] font-medium">Weekly team summary</summary>
        <table className="w-full mt-2 text-[11px]"><thead><tr className="text-left text-slate-500"><th>Week</th><th className="text-right">All goals met</th><th className="text-right">MG1s</th><th className="text-right">Launches</th><th className="text-right">Newly qualified</th></tr></thead>
          <tbody>{data.weekly.map((w) => <tr key={w.index} className="border-t border-slate-100"><td className="py-1">{weekLabel(w)}{w.upcoming ? " · upcoming" : ""}</td>
            <td className="text-right">{w.goals_all_met}</td><td className="text-right">{w.mg1s}</td><td className="text-right">{w.launches}</td><td className="text-right">{w.newly_qualified}</td></tr>)}</tbody></table>
      </details>}
    </div>
  );
}
